import axios from 'axios';
import { FC } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Form, message, Modal, Select, Button, Flex, Badge } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useBackoffice } from '../../backoffice/useBackoffice';
import { KycVerification } from '../useCustomers';

export const EditKYCVerificationModal: FC<{
  customerId: string;
  kycVerification: KycVerification;
  open: boolean;
  onClose: () => void;
}> = ({ open, onClose, customerId, kycVerification }) => {
  const { developerId } = useBackoffice();
  const { getAccessTokenSilently } = useAuth0();
  const [messageApi, contextHolder] = message.useMessage();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationKey: ['edit-kyc-verification'],
    mutationFn: async (payload: Partial<KycVerification>) => {
      const token = await getAccessTokenSilently();
      const res = await axios.put(
        `${process.env.REACT_APP_BACKEND_API_URL}/customers/${customerId}/kyc-verification`,
        payload,
        {
          params: {
            consumerId: developerId,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return res.data?.data;
    },
  });

  const [form] = Form.useForm();

  const onFinish = (values: Partial<KycVerification>) => {
    mutation
      .mutateAsync(values)
      .then(() => {
        messageApi.success('Successfully edited!');
        onClose();
        return queryClient.invalidateQueries({
          queryKey: ['customers'],
        });
      })
      .catch((err) => {
        messageApi.error(
          err.response.status === 424 ? `Error: ${err?.response?.data?.error}` : 'Something is wrong',
          6000
        );
      });
  };

  return (
    <>
      {contextHolder}
      <Modal
        loading={mutation.isPending}
        title="Edit KYC Verification"
        destroyOnClose
        footer={null}
        open={open}
        onCancel={onClose}
      >
        <Form
          onFinish={onFinish}
          name="editKYCVerification"
          form={form}
          initialValues={{
            type: kycVerification.type,
            kyc_status: kycVerification.kyc_status,
          }}
          requiredMark={false}
          style={{
            paddingTop: 20,
          }}
          layout="vertical"
          autoComplete="off"
        >
          <Form.Item
            rules={[
              {
                required: true,
                message: 'Please select',
              },
            ]}
            name="type"
            layout="vertical"
            label="Type"
          >
            <Select placeholder="Select value...">
              <Select.Option value="general">General</Select.Option>
              <Select.Option value="bridge">Bridge</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            rules={[
              {
                required: true,
                message: 'Please select',
              },
            ]}
            name="kyc_status"
            layout="vertical"
            label="Status"
          >
            <Select placeholder="Select value...">
              <Select.Option value="approved">
                <Badge text={'Approved'} status={'success'} />
              </Select.Option>
              <Select.Option value="pending">
                <Badge text={'Pending'} status={'warning'} />
              </Select.Option>
              <Select.Option value="rejected">
                <Badge text={'Rejected'} status={'warning'} />
              </Select.Option>
            </Select>
          </Form.Item>

          <Flex style={{ paddingTop: '18px' }} gap={'middle'} justify="flex-end">
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" htmlType="submit">
              Edit
            </Button>
          </Flex>
        </Form>
      </Modal>
    </>
  );
};
